import {createReducer, on} from '@ngrx/store';
import {autoSaveRequestFailed, autoSaveRequestFinished, autoSaveRequestStarted} from './auto-save.actions';

export interface AutoSaveState {
  operations: {[operationName: string]: OperationStatus};
  lastOperationFailed: boolean;
}

export interface OperationStatus {
  pending: PendingRequest[];
}

export interface PendingRequest {
  /** global unique id */
  id: string;
  operationName: string;
  /**
   * an auto save request with an equivalenceClass automatically finishes all previous requests for the same operationName and equivalenceClass.
   * Use this when finishing requests due to switchMap is not feasible.
   **/
  equivalenceClass?: string;
  startedTs: string;
}

export interface AutoSaveError {
  status?: number;
  i18nToken: string;
  errorContent: string;
}

export function isAutoSaveError(x: any): x is AutoSaveError {
  return !!x && x instanceof Object && 'i18nToken' in x && 'errorContent' in x;
}

export const initialState: AutoSaveState = {
  operations: {},
  lastOperationFailed: false
};

export const autoSaveReducer = createReducer(
  initialState,

  on(autoSaveRequestStarted, (state, action) => {
    const form = getForm(state, action.payload.operationName);
    return {
      ...state,
      operations: {
        ...state.operations,
        [action.payload.operationName]: {
          ...form,
          pending: [...form.pending.filter(r => !r.equivalenceClass || r.equivalenceClass !== action.payload.equivalenceClass), action.payload]
        }
      }
    };
  }),
  on(autoSaveRequestFinished, (state, action) => {
    const form2 = getForm(state, action.payload.operationName);
    return {
      ...state,
      operations: {
        ...state.operations,
        [action.payload.operationName]: {
          ...form2,
          pending: form2.pending.filter(p => p.id !== action.payload.id)
        }
      },
      lastOperationFailed: false
    };
  }),
  on(autoSaveRequestFailed, (state, action) => {
    const form3 = getForm(state, action.request.operationName);
    return {
      ...state,
      operations: {
        ...state.operations,
        [action.request.operationName]: {
          ...form3,
          pending: form3.pending.filter(p => p.id !== action.request.id)
        }
      },
      lastOperationFailed: true
    };
  })
);

function getForm(state: AutoSaveState, operationName: string): OperationStatus {
  return state.operations[operationName] ?? {pending: []};
}
